import { useState } from "react";

import { useApiReadingKeysReveal, useApiReadingKeysRevoke } from "modules/ApiKeys/service";

type ApiReadingKeysModals = {
  showApiKeyModal: boolean;
  createApiKeyModal: boolean;
  revokeApiKeyModal: boolean;
  requestLogsModal: boolean;
};

export enum API_KEY_MODALS {
  ShowApiKeyModal = "showApiKeyModal",
  CreateApiKeyModal = "createApiKeyModal",
  RevokeApiKeyModal = "revokeApiKeyModal",
  RequestLogsModal = "requestLogsModal"
}

export const useApiKeys = () => {
  const [{ createApiKeyModal, revokeApiKeyModal, showApiKeyModal, requestLogsModal }, setModal] = useState<ApiReadingKeysModals>({
    showApiKeyModal: false,
    createApiKeyModal: false,
    revokeApiKeyModal: false,
    requestLogsModal: false
  });
  const [apiKeyReasonNote, setApiKeyReasonNote] = useState("");
  const [revealApiKeyID, setRevealApiKeyID] = useState<number | null>(null);
  const [requestLogsID, setRequestLogsID] = useState<number | null>(null);
  const [revokeApiKeyID, setRevokeApiKeyID] = useState<number | null>(null);

  const { mutateAsync: apiReadingKeysRevokeMutation, isPending: isRevokeLoading } = useApiReadingKeysRevoke();
  const { mutateAsync: apiReadingKeysRevealMutation, isPending: isRevealLoading } = useApiReadingKeysReveal(revealApiKeyID);

  const handleOpenModal = (modalName: API_KEY_MODALS) => setModal(prevModals => ({ ...prevModals, [modalName]: true }));
  const handleCloseModal = (modalName: API_KEY_MODALS) => setModal(prevModals => ({ ...prevModals, [modalName]: false }));

  const handleRequestsLogsModal = (id: number) => {
    setRequestLogsID(id);
    handleOpenModal(API_KEY_MODALS.RequestLogsModal);
  };

  const handleRevokeApiKeyModal = (id: number) => {
    setRevokeApiKeyID(id);
    handleOpenModal(API_KEY_MODALS.RevokeApiKeyModal);
  };

  const handleShowApiKeyModal = (id: number) => {
    setRevealApiKeyID(id);
    handleOpenModal(API_KEY_MODALS.ShowApiKeyModal);
  };

  const handleRevokeApiKey = async () => {
    if (!revokeApiKeyID) return handleCloseModal(API_KEY_MODALS.RevokeApiKeyModal);

    await apiReadingKeysRevokeMutation(revokeApiKeyID);
    handleCloseModal(API_KEY_MODALS.RevokeApiKeyModal);
  };

  const handleCancelRevokeApiKey = () => {
    setRevokeApiKeyID(null);
    handleCloseModal(API_KEY_MODALS.RevokeApiKeyModal);
  };

  const handleConfirmShowApiKey = async () => {
    revealApiKeyID && (await apiReadingKeysRevealMutation({ reason: apiKeyReasonNote, ID: revealApiKeyID }));
    handleCloseModal(API_KEY_MODALS.ShowApiKeyModal);
    setApiKeyReasonNote("");
    setRevealApiKeyID(null);
  };

  const handleCancelShowApiKey = () => {
    setRevealApiKeyID(null);
    setApiKeyReasonNote("");
    handleCloseModal(API_KEY_MODALS.ShowApiKeyModal);
  };

  return {
    showApiKeyModal,
    createApiKeyModal,
    revokeApiKeyModal,
    requestLogsModal,
    handleCloseModal,
    handleOpenModal,
    handleRequestsLogsModal,
    handleRevokeApiKeyModal,
    handleShowApiKeyModal,
    handleRevokeApiKey,
    handleCancelRevokeApiKey,
    handleConfirmShowApiKey,
    handleCancelShowApiKey,
    setApiKeyReasonNote,
    requestLogsID,
    apiKeyReasonNote,
    isRevokeLoading,
    isRevealLoading
  };
};
